import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';

export interface DepreciationSchedule {
  depreciation_id: number;
  year_number: number;
  opening_book_value: number;
  depreciation_amount: number;
  accumulated_depreciation: number;
  closing_book_value: number;
  depreciation_date: string;
}

export interface AssetData {
  asset_id: number;
  asset_tag: string;
  asset_name: string;
  purchase_cost: number;
  current_value: number;
  salvage_value: number;
  useful_life_years: number;
  depreciation_method: string;
  purchase_date: string;
  accumulated_depreciation: number;
}

@Component({
  selector: 'app-depreciation-dialog',
  templateUrl: './depreciation-dialog.component.html',
  styleUrls: ['./depreciation-dialog.component.scss']
})
export class DepreciationDialogComponent implements OnInit {
  displayedColumns: string[] = [
    'year_number', 'opening_book_value', 'depreciation_amount', 
    'accumulated_depreciation', 'closing_book_value', 'depreciation_date'
  ];
  
  dataSource = new MatTableDataSource<DepreciationSchedule>();
  depreciationSchedules: DepreciationSchedule[] = [];
  loading = false;
  assetData: AssetData;

  constructor(
    private dialogRef: MatDialogRef<DepreciationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssetData,
    private http: HttpClient
  ) {
    this.assetData = data;
  }

  ngOnInit(): void {
    this.loadDepreciationSchedule();
  }

  loadDepreciationSchedule(): void {
    this.loading = true;
    this.http.get<DepreciationSchedule[]>(`/api/v1/hawdco/assets/${this.assetData.asset_id}/depreciation-schedule`).subscribe({
      next: (data) => {
        this.depreciationSchedules = data;
        this.dataSource.data = data;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading depreciation schedule:', error);
        this.loading = false;
      }
    });
  }

  calculateDepreciation(): void {
    this.loading = true;
    this.http.post(`/api/v1/hawdco/assets/${this.assetData.asset_id}/calculate-depreciation`, {}).subscribe({
      next: () => {
        this.loadDepreciationSchedule();
      },
      error: (error) => {
        console.error('Error calculating depreciation:', error);
        this.loading = false;
      }
    });
  }

  exportDepreciationReport(): void {
    this.http.get(`/api/v1/hawdco/assets/${this.assetData.asset_id}/depreciation-report`, { 
      responseType: 'blob' 
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `depreciation-report-${this.assetData.asset_tag}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error exporting depreciation report:', error);
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  // Calculate remaining useful life
  getRemainingLife(): number {
    const currentYear = new Date().getFullYear();
    const purchaseYear = new Date(this.assetData.purchase_date).getFullYear();
    const yearsInUse = currentYear - purchaseYear;
    return Math.max(0, this.assetData.useful_life_years - yearsInUse);
  }

  // Calculate annual depreciation amount
  getAnnualDepreciation(): number {
    const depreciableAmount = this.assetData.purchase_cost - this.assetData.salvage_value;
    return depreciableAmount / this.assetData.useful_life_years;
  }

  // Get depreciation progress percentage
  getDepreciationProgress(): number {
    if (this.assetData.accumulated_depreciation && this.assetData.purchase_cost) {
      return (this.assetData.accumulated_depreciation / this.assetData.purchase_cost) * 100;
    }
    return 0;
  }
}
